import React, { useState, useEffect } from "react";
import { Variant } from "../../../../types/ProductTypes";
import { numberWithCommas, allowNumbersOnly } from "../../../../shared/utils";
import TrashIcon from "../../../../assets/icons/ic_delete.svg";
import AddImg from "../../../../assets/add-img.svg";
import DatePicker from "react-datepicker";
import { ProductImagePopUp } from "./ProductImagePopUp";
import NotificationPopup from "../NotificationPopup";
import "./ProductVarianceModal.scss";

interface ProductVarianceModalProps {
  show: boolean;
  onClose: () => void;
  variants: Variant[];
  setVariants: (variant: Variant[]) => void;
  pageProps: any;
  sizeOfFetch: number;
  editIndex?: number | null;
  initialEditVariant?: Variant | null;
}

const getImagesGallery = async ({ pageProps, payload }: any) => {
  await pageProps.getImageGalleryAction(payload, false);
};

let imagesChosen: any = [];

const ProductVarianceModal = ({
  show,
  onClose,
  variants,
  setVariants,
  pageProps,
  sizeOfFetch,
  editIndex = null,
  initialEditVariant = null,
}: ProductVarianceModalProps) => {
  const [filingVariants, setFillingVariants] = useState<Variant[]>(variants);
  const [isEdtting, setIsEditting] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<null | number>(null);
  const [showImagesModal, setShowImagesModal] = useState<boolean>(false);
  const [catListItems, setCatListItems] = useState<any[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentVariant, setCurrentVariant] = useState<Variant>({
    category: "color",
    value: "",
    optionalUnit: undefined,
    stockQuantity: 0,
    price: {
      amount: 0,
    },
    addDiscount: false,
    discountPercentage: undefined,
    discountPrice: undefined,
    images: [], // max 3
  });

  const handleAddVariant = (e: any) => {
    e.preventDefault();
    const updatedVariants = [...filingVariants];
    if (
      filingVariants.some(
        (v) =>
          v.value === currentVariant.value &&
          v.category === currentVariant.category &&
          !isEdtting,
      )
    ) {
      setShowModal(true);
      return;
    }

    if (editingIndex !== null) {
      updatedVariants[editingIndex] = currentVariant;
      setEditingIndex(null);
    } else {
      updatedVariants.push(currentVariant);
    }

    setFillingVariants(updatedVariants);
    setCurrentVariant({
      category: "color",
      value: "",
      optionalUnit: undefined,
      stockQuantity: 0,
      price: {
        amount: 0,
      },
      addDiscount: false,
      discountPercentage: undefined,
      discountPrice: undefined,
      images: [], // max 3
    });
    setIsEditting(false);
  };

  const handleEdit = (index: number) => {
    setCurrentVariant(filingVariants[index]);
    setIsEditting(true);
    setEditingIndex(index);
  };
  const handleDelete = (index: number) => {
    const updatedVariants = filingVariants.filter((_, i) => i !== index);
    setFillingVariants(updatedVariants);

    if (editingIndex === index) {
      setCurrentVariant({
        category: "color",
        value: "",
        optionalUnit: undefined,
        stockQuantity: 0,
        price: {
          amount: 0,
        },
        addDiscount: false,
        discountPercentage: undefined,
        discountPrice: undefined,
        images: [], // max 3
      });
      setEditingIndex(null);
    } else if ((editingIndex as number) > index) {
      setEditingIndex((editingIndex as number) - 1);
    }
  };
  const handleUploadImagesUpdate = (newImages: any[]) => {
    setCurrentVariant((prev) => {
      const existingImages = prev.images || [];
      const totalImages = existingImages.length + newImages.length;

      let updatedImages = [...existingImages, ...newImages];

      if (totalImages > 3) {
        const excess = totalImages - 3;
        updatedImages = updatedImages.slice(excess);
      }

      return {
        ...prev,
        images: updatedImages,
      };
    });
  };

  const removeProductImage = (url: any) => {
    let imagesBatch = currentVariant.images;

    imagesBatch = imagesBatch.filter((itemData: any) => {
      return itemData.url !== url;
    });
    setCurrentVariant((prev) => ({
      ...prev,
      images: [],
    }));
    setCurrentVariant((prev) => ({
      ...prev,
      images: [...imagesBatch],
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value, type, checked } = e.target;

    let processedValue: any = type === "checkbox" ? checked : value;

    // Convert number inputs to actual numbers and update nested price object
    if (type === "number" && name === "price") {
      const amount = value === "" ? 0 : parseFloat(value);
      setCurrentVariant((prev) => ({
        ...prev,
        price: {
          ...prev.price,
          amount,
        },
      }));
      return;
    }

    if (type === "number" && name === "stockQuantity") {
      processedValue = value === "" ? 0 : parseFloat(value);
    }

    setCurrentVariant((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };
  const handleDiscountInputChange = (
    name: string,
    value: string | number | Date,
  ) => {
    setCurrentVariant((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  // Handle prefilling when opened with edit data from external button
  useEffect(() => {
    if (show && initialEditVariant && editIndex !== null) {
      setCurrentVariant(initialEditVariant);
      setEditingIndex(editIndex);
      setIsEditting(true);
    } else if (show && !isEdtting) {
      // Reset if opening in "add" mode but was previously editing
      setCurrentVariant({
        category: "color",
        value: "",
        optionalUnit: undefined,
        stockQuantity: 0,
        price: {
          amount: 0,
        },
        addDiscount: false,
        discountPercentage: undefined,
        discountPrice: undefined,
        images: [],
      });
      setEditingIndex(null);
      setIsEditting(false);
    }
  }, [show, initialEditVariant, editIndex]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && show) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <>
      <div className="custom-modal-backdrop" onClick={onClose}></div>
      <div className="custom-modal variant-modal">
        <div className="custom-modal-dialog">
          <div className="custom-modal-header">
            <h2 className="custom-modal-title variance-header">
              Add a Product Variance
            </h2>
            <button
              className="custom-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <div className="custom-modal-body">
            <p className="variance-description">
              Add the product variance below. Variance can be by Color, Size,
              and Count.
            </p>
            {filingVariants.length > 0 && (
              <div className="variant-summary">
                {filingVariants.map((variant, index) => (
                  <div key={index} className="variant-card">
                    <div className="variant-left">
                      <div>
                        <div className="variant-label">
                          Variance {index + 1}
                        </div>
                      </div>
                      <div className="variant-chip-container">
                        <div className="variant-chip">
                          {variant.category}: {variant.value}
                        </div>
                        <div className="variant-chip">
                          Price: ${variant.price.amount}
                        </div>
                        <div className="variant-chip">
                          Stock Qty: {variant.stockQuantity}
                        </div>
                        {variant.discountPrice?.amount && (
                          <div className="variant-chip discount-chip">
                            Discount: ${variant.discountPrice.amount}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="variant-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(index)}
                      >
                        ✎
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(index)}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form className="variant-form" onSubmit={handleAddVariant}>
              <div className="two-shared-box">
                <div>
                  <label htmlFor="category" className="tax-label">
                    Variance
                  </label>
                  <select
                    name="category"
                    value={currentVariant.category}
                    className="input-wrap inapp-input"
                    onChange={(e) => handleInputChange(e)}
                  >
                    <option value="">Select Variance</option>
                    <option value="color">Color</option>
                    <option value="length">Length</option>
                    <option value="weight">Weight</option>
                  </select>
                </div>
                <div className="two-shared-box">
                  <div>
                    <label htmlFor="value" className="tax-label">
                      Value
                    </label>
                    <input
                      name="value"
                      required
                      placeholder={
                        currentVariant.category === "color"
                          ? "E.g. Red"
                          : "E.g 30"
                      }
                      className="input-wrap inapp-input"
                      value={currentVariant.value}
                      onChange={handleInputChange}
                    />
                  </div>
                  {(currentVariant.category === "length" ||
                    currentVariant.category === "weight") && (
                    <div>
                      <label htmlFor="optionalUnit" className="tax-label">
                        Unit
                      </label>
                      <select
                        name="optionalUnit"
                        value={currentVariant.optionalUnit}
                        className="input-wrap inapp-input"
                        onChange={handleInputChange}
                      >
                        {currentVariant.category === "length" ? (
                          <>
                            <option value="cm">Centimeters</option>
                            <option value="m">Meters</option>
                          </>
                        ) : (
                          <>
                            <option value="g">Grams</option>
                            <option value="kg">Kilograms</option>
                            <option value="lbs">Pounds</option>
                          </>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="two-shared-box">
                <div>
                  <label htmlFor="price" className="tax-label">
                    Price
                  </label>
                  <div className="price-wrap">
                    <span className="currency-pre">$</span>
                    <input
                      type="number"
                      name="price"
                      value={currentVariant.price.amount}
                      required
                      onChange={handleInputChange}
                      className="input-wrap inapp-input"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="stockQuantitiy" className="tax-label">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    name="stockQuantity"
                    placeholder="Stock Quantity"
                    value={currentVariant.stockQuantity}
                    required
                    onChange={handleInputChange}
                    className="input-wrap inapp-input"
                  />
                </div>
              </div>

              <div className="two-shared-box">
                <div className="variant-image-upload-wrapper">
                  <div className="images-label">Product image</div>
                  <div
                    className="product-images-upload-box"
                    onClick={() => {
                      let allCatData: any = [];
                      pageProps.getProductMetaDataRequest.request_data.response.categories.forEach(
                        (eachCat: any) => {
                          allCatData.push({
                            value: eachCat.name,
                            label: eachCat.name,
                          });
                        },
                      );
                      imagesChosen.length = 1;
                      setCatListItems(allCatData);
                      let payload = `page=1&size=${sizeOfFetch}`;
                      getImagesGallery({ pageProps, payload });

                      setShowImagesModal(true);
                    }}
                  >
                    <div className="upload-wrap-content">
                      <img src={AddImg} alt="" />
                      <div className="upload-txt">
                        <span>Check MamiMarkets Gallery</span>
                        <small>or</small>
                        <span>Upload from your device</span>
                      </div>
                    </div>
                  </div>

                  {currentVariant.images.length >= 1 && (
                    <div className="variant-images-scroller">
                      {currentVariant.images.map(
                        (eachImage: any, index: any) => {
                          return (
                            <div className="product-image-wrap" key={index}>
                              <div className="image-actions">
                                <div className="select-cover">
                                  <input
                                    type="radio"
                                    name="cover-image-select"
                                  />
                                </div>
                                <div
                                  className="deleteicon"
                                  onClick={() =>
                                    removeProductImage(eachImage.url)
                                  }
                                >
                                  <img src={TrashIcon} alt="" className="" />
                                </div>
                              </div>
                              <div className="product-image-item">
                                <img src={eachImage.url} alt="" />
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </div>
                <div className="discount-taxable-box">
                  <div className="checkbox-radio-wrap">
                    <label>
                      <input
                        type="checkbox"
                        name="addDiscount"
                        checked={currentVariant.addDiscount}
                        onChange={handleInputChange}
                      />
                      Add a discount
                    </label>
                  </div>
                  {currentVariant.addDiscount && (
                    <div className="discount-wrap">
                      <div className="two-shared-box">
                        <div className="form-group">
                          <div>
                            <label htmlFor="discountPercentage">
                              Discount %
                            </label>
                            <input
                              disabled={!currentVariant.price.amount}
                              required
                              value={
                                allowNumbersOnly(
                                  (
                                    currentVariant.discountPercentage ?? ""
                                  ).toString(),
                                  3,
                                ) ?? ""
                              }
                              name="discountPercentage"
                              type="number"
                              placeholder="Discount %"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                let valueSet: any = numberWithCommas(
                                  e.target.value,
                                );
                                if (
                                  parseFloat(valueSet) <= 100 ||
                                  e.target.value === ""
                                ) {
                                  const numericPercent =
                                    parseFloat(valueSet) || 0;
                                  handleDiscountInputChange(
                                    "discountPercentage",
                                    numericPercent,
                                  );

                                  let originalPrice =
                                    currentVariant.price.amount;

                                  let newProductPrice =
                                    (numericPercent / 100) * originalPrice;
                                  newProductPrice =
                                    originalPrice - newProductPrice;

                                  // Create nested discountPrice object
                                  setCurrentVariant((prev) => ({
                                    ...prev,
                                    discountPrice: {
                                      amount: parseFloat(
                                        newProductPrice.toFixed(2),
                                      ),
                                    },
                                  }));
                                }
                              }}
                              className="input-wrap inapp-input"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <div>
                            <label htmlFor="productDiscountPrice">
                              New price
                            </label>

                            <div className="price-wrap">
                              <span className="currency-pre">$</span>
                              <input
                                value={numberWithCommas(
                                  (
                                    currentVariant.discountPrice?.amount ?? 0
                                  ).toString(),
                                )}
                                name="productDiscountPrice"
                                type="text"
                                disabled={!currentVariant.price.amount}
                                required
                                onChange={(e: any) => {
                                  let valueSet: any = numberWithCommas(
                                    e.target.value,
                                  );

                                  // Convert to number for storage
                                  const numericValue = valueSet.replace(
                                    /,/g,
                                    "",
                                  );

                                  // Create nested discountPrice object
                                  setCurrentVariant((prev) => ({
                                    ...prev,
                                    discountPrice: {
                                      amount: parseFloat(numericValue) || 0,
                                    },
                                  }));

                                  if (
                                    parseFloat(numericValue) >=
                                    currentVariant.price.amount
                                  ) {
                                    handleDiscountInputChange(
                                      "discountPercentage",
                                      0,
                                    );
                                  } else {
                                    let originalPrice =
                                      currentVariant.price.amount;

                                    let diff =
                                        originalPrice -
                                        parseFloat(numericValue),
                                      perCentOffer = Math.round(
                                        (diff / originalPrice) * 100,
                                      );
                                    handleDiscountInputChange(
                                      "discountPercentage",
                                      perCentOffer,
                                    );
                                  }
                                }}
                                className="input-wrap inapp-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="two-shared-box">
                        <div className="form-group">
                          <div>
                            <label htmlFor="discountStartDate">
                              Start date
                            </label>

                            <DatePicker
                              selected={
                                currentVariant.discountPrice?.startDate
                                  ? new Date(
                                      currentVariant.discountPrice.startDate,
                                    )
                                  : null
                              }
                              showIcon={true}
                              className="input-wrap inapp-input"
                              required
                              onChange={(date: Date) => {
                                const isoDate = date?.toISOString();
                                setCurrentVariant((prev) => ({
                                  ...prev,
                                  discountPrice: {
                                    ...prev.discountPrice,
                                    amount: prev.discountPrice?.amount || 0,
                                    startDate: isoDate,
                                  },
                                }));
                              }}
                              minDate={new Date()}
                              name="discountStartDate"
                              placeholderText="Select start date"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <div>
                            <label htmlFor="discountEndDate">End date</label>
                            <DatePicker
                              showIcon={true}
                              required
                              className="input-wrap inapp-input"
                              selected={
                                currentVariant.discountPrice?.dueDate
                                  ? new Date(
                                      currentVariant.discountPrice.dueDate,
                                    )
                                  : null
                              }
                              onChange={(date: Date) => {
                                const isoDate = date?.toISOString();
                                setCurrentVariant((prev) => ({
                                  ...prev,
                                  discountPrice: {
                                    ...prev.discountPrice,
                                    amount: prev.discountPrice?.amount || 0,
                                    dueDate: isoDate,
                                  },
                                }));
                              }}
                              name="discountEndDate"
                              minDate={new Date()}
                              placeholderText="Select end date"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button className="add-another" type="submit">
                + {isEdtting ? "Save New Changes" : "Add Another Variant"}
              </button>
            </form>

            <div className="product-cta">
              <button
                className="add-main"
                disabled={
                  filingVariants.length === 0 &&
                  (!currentVariant.value ||
                    !currentVariant.price.amount ||
                    !currentVariant.stockQuantity)
                }
                onClick={() => {
                  let finalVariants = [...filingVariants];

                  // If current variant is filled out and not already in editing mode, add it
                  if (
                    currentVariant.value &&
                    currentVariant.price.amount &&
                    currentVariant.stockQuantity &&
                    editingIndex === null
                  ) {
                    // Check for duplicates
                    const isDuplicate = filingVariants.some(
                      (v) =>
                        v.value === currentVariant.value &&
                        v.category === currentVariant.category,
                    );

                    if (!isDuplicate) {
                      finalVariants.push(currentVariant);
                    }
                  }

                  setVariants(finalVariants);
                  onClose();
                }}
              >
                Add Variance
              </button>
            </div>
          </div>
        </div>
      </div>
      {showImagesModal && (
        <ProductImagePopUp
          pageProps={pageProps}
          showImagesModal={showImagesModal}
          uploadImagesData={currentVariant.images}
          setUploadImagesData={handleUploadImagesUpdate}
          catListItems={catListItems}
          hideProductImgModal={() => setShowImagesModal(false)}
        />
      )}
      {showModal && (
        <NotificationPopup
          message="A variant with the same value and category already exists."
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default ProductVarianceModal;
